import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useSiteSettings } from '@/lib/siteSettings';

export default function ContactCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { settings } = useSiteSettings();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = formData.get('company_alt') as string;
    if (hp && hp.trim() !== '') {
      setSubmitted(true);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qvqtknlsngrm4lm6qg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitted(true);
        setError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-accent-300"></div>

      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
            >
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">
                Get In Touch
              </span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-5 leading-tight">
                Let&apos;s Build the<br />Future Together
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                Every successful partnership begins with a conversation. Whether you&apos;re sourcing quality products, managing complex logistics, or seeking a dependable procurement partner, we are ready to help.
              </p>

              <div className="space-y-5">
                {[
                  { icon: 'ri-mail-line', label: 'Email', value: settings.contact_email, href: `mailto:${settings.contact_email}` },
                  { icon: 'ri-phone-line', label: 'Phone', value: settings.contact_phone, href: `tel:${settings.contact_phone}` },
                  { icon: 'ri-map-pin-line', label: 'Headquarters', value: settings.contact_address, href: null },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className={item.icon}></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-0.5">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors duration-200">
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-sm font-medium text-foreground-800">{item.value}</span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-background-100 border border-background-200 rounded-2xl p-10 text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-2">Thank You!</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Your inquiry has been received. Our team will get back to you within 24 hours.
                  </p>
                </motion.div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form="true"
                  className="bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-8"
                >
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 placeholder:text-foreground-300"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Company *</label>
                        <input
                          type="text"
                          name="company"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 placeholder:text-foreground-300"
                          placeholder="Your Company Ltd"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Email *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 placeholder:text-foreground-300"
                          placeholder="you@company.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 placeholder:text-foreground-300"
                          placeholder="+254 700 000 000"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">Product / Service Interest</label>
                      <select
                        name="interest"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 cursor-pointer"
                      >
                        <option value="">Select an option</option>
                        <option value="trading">Trading</option>
                        <option value="procurement">Procurement</option>
                        <option value="logistics">Logistics</option>
                        <option value="freight">Freight Forwarding</option>
                        <option value="clearing">Clearing & Forwarding</option>
                        <option value="other">Other / Multiple</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">Message *</label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 resize-none text-foreground-800 placeholder:text-foreground-300"
                        placeholder="Tell us about your requirements..."
                      ></textarea>
                    </div>

                    <div className="hp-field">
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    {error && (
                      <div className="text-xs text-red-500 bg-red-50 px-4 py-2.5 rounded-lg">{error}</div>
                    )}

                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full px-6 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Send Inquiry
                      <i className="ri-send-plane-line ml-2"></i>
                    </motion.button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}